/**
 * HOT competitor comparison cluster (SEO Master Document, section 8).
 *
 * Facts about third-party products are sourced from their own public rules
 * pages and carry a review date. Live numbers change: nothing here is
 * presented as a permanent claim.
 */

export type CompareRow = { dimension: string; behot: string; them: string };

export type HotComparison = {
  /** URL segment under /compare/hot/ */
  slug: string;
  brand: string;
  title: string;
  h1: string;
  description: string;
  keywords: string[];
  /** Public source used for the competitor facts. */
  source?: string;
  reviewed?: string;
  intro: string;
  summary: string[];
  rows: CompareRow[];
  faq: [string, string][];
  related: string[];
};

const REVIEWED = "27 August 2026";

const OUTBID_LOL_ROWS: CompareRow[] = [
  {
    dimension: "Ranking rule",
    behot: "Heat based ranking according to the BeHot.Now board rules, with owner boosts and fan boosts.",
    them: "Official rules say rank is the bid and nothing else.",
  },
  {
    dimension: "Board structure",
    behot: "Multiple category specific boards.",
    them: "All time and Today leaderboards plus category rankings.",
  },
  {
    dimension: "What can be listed",
    behot: "Many eligible entity types across the BeHot.Now categories.",
    them: "A product website or an X handle according to current official rules.",
  },
  {
    dimension: "Public profile",
    behot: "A connected BeHot.Now user or professional profile behind every listing.",
    them: "Public listing with destination, description, bid total, position, category and click count where available.",
  },
  {
    dimension: "Marketplace",
    behot: "Connected BeHot.Now Marketplace for buying and selling services.",
    them: "Not a full freelance and creator services marketplace.",
  },
  {
    dimension: "Promote",
    behot: "Connected Promote layer for campaigns, playlist pitching and ads.",
    them: "The leaderboard itself is the paid placement product.",
  },
  {
    dimension: "Earn",
    behot: "Connected earning layer with an Earn wallet and payouts.",
    them: "No equivalent full creator and seller earning ecosystem shown as a core product.",
  },
  {
    dimension: "Referral",
    behot: "Connected referral program paying Reward Heat.",
    them: "No equivalent referral ecosystem shown as a core product.",
  },
  {
    dimension: "Fund",
    behot: "Connected funding layer for eligible projects.",
    them: "No equivalent connected funding layer shown as a core product.",
  },
  {
    dimension: "Languages",
    behot: "Eight languages.",
    them: "Check the current public experience per locale.",
  },
  {
    dimension: "New listing minimum",
    behot: "Set per board by BeHot.Now category pricing, shown before you pay.",
    them: `US$5 minimum according to official rules checked ${REVIEWED}.`,
  },
  {
    dimension: "Taking number one",
    behot: "Depends on the Heat behind the current leader and how long their boost has left.",
    them: "At least US$5 more than the current top bid according to official rules.",
  },
  {
    dimension: "Daily ranking",
    behot: "Board timing and boost duration are set per board.",
    them: "Today ranks spending in the last 24 hours; a payment counts for a day, then drops from Today while still adding to the all time bid.",
  },
  {
    dimension: "Raising an existing listing",
    behot: "Boost the same listing at any time from your wallet.",
    them: "The same URL or handle can raise its bid; checkout charges the difference according to official rules.",
  },
  {
    dimension: "Tracking links",
    behot: "Referral and tracking behaviour follows the BeHot.Now product rules.",
    them: "Query parameters are stripped from listing links according to official rules.",
  },
  {
    dimension: "Best for",
    behot: "People who want ranking plus a wider attention and commerce ecosystem.",
    them: "A simple public bid driven leaderboard where the paid amount directly determines position.",
  },
];

export const HOT_COMPARISONS: HotComparison[] = [
  {
    slug: "outbid-lol",
    brand: "Outbid.lol",
    title: "Outbid.lol Alternative | BeHot.Now HOT Boards vs Outbid.lol",
    h1: "Looking for an Outbid.lol alternative?",
    description:
      "BeHot.Now vs Outbid.lol: how the two pay to rank leaderboards compare on ranking rules, pricing, listings, profiles, marketplace, earning and languages.",
    keywords: [
      "outbid.lol alternative",
      "outbid lol alternative",
      "outbid.lol competitors",
      "sites like outbid.lol",
      "outbid.lol vs BeHot.now",
      "outbid.lol pricing",
      "outbid.lol cost",
      "outbid.lol ranking",
      "pay to rank alternative",
    ],
    source: "https://outbid.lol/rules",
    reviewed: REVIEWED,
    intro:
      "Outbid.lol is the reference product for the current public pay to rank leaderboard wave, and it is deliberately simple: its official rules say rank is the bid and nothing else. If that is exactly what you want, it does the job. This page is for people who want the ranking and everything that should sit around it.",
    summary: [
      "BeHot.Now also lets eligible listings compete for attention, but the ranking sits inside a larger attention marketplace. A listing can connect to a public profile, to services people can buy, to promotion campaigns, to an earning and payout layer, to a referral program and to funding.",
      "The other structural difference is what can be listed. Outbid.lol focuses on a product website or an X handle. BeHot.Now runs category boards for products, apps, startups, AI, creators, influencers, music, artists, DJs, brands, businesses, events, gaming and social accounts.",
      "Both are transparent about position. BeHot.Now adds fan boosts, so supporters can move a listing up without the owner spending more.",
    ],
    rows: OUTBID_LOL_ROWS,
    faq: [
      ["Is BeHot.Now a copy of Outbid.lol?", "No. Both run public paid leaderboards, but BeHot.Now connects ranking to profiles, a services marketplace, promotion, earning, referrals and funding rather than being a standalone leaderboard."],
      ["What does Outbid.lol cost?", `Its official rules listed a US$5 minimum for a new listing, and at least US$5 above the current top bid to take the number one spot, when checked on ${REVIEWED}. Always check the current rules page.`],
      ["What does BeHot.Now cost?", "Each board has its own minimum boost, shown before you pay. You pay from your wallet, never directly by card at checkout."],
      ["Can I use both?", "Yes. They are different surfaces with different audiences."],
      ["Which one is better for a creator?", "BeHot.Now, because creators get a public indexed profile, can be boosted by fans and can sell services and take payouts from the same account."],
    ],
    related: ["paytorank", "productleaderboards", "outbidto", "producthunt"],
  },
  {
    slug: "outbidto",
    brand: "Outbid.to",
    title: "BeHot.Now vs Outbid.to — Paid Leaderboard Comparison",
    h1: "BeHot.Now or Outbid.to?",
    description:
      "Compare BeHot.Now and Outbid.to: single leaderboard versus category boards, minimum bids, what can be listed and what happens after you rank.",
    keywords: ["outbid.to alternative", "outbid.to comparison", "outbid to alternative"],
    source: "https://www.outbid.to/",
    reviewed: REVIEWED,
    intro:
      "Outbid.to runs one public leaderboard where the bid determines rank. It is the leanest version of the format.",
    summary: [
      `When checked on ${REVIEWED}, Outbid.to showed a minimum bid of US$2 on a single public board.`,
      "BeHot.Now splits attention across category boards, so a music release is not competing against an enterprise SaaS product for the same slot, and every listing has a public profile, services and payouts behind it.",
    ],
    rows: [
      { dimension: "Boards", behot: "Category specific boards plus a general view.", them: "One public leaderboard." },
      { dimension: "Ranking rule", behot: "Heat behind the listing, from the owner and from fans.", them: "Bid determines rank." },
      { dimension: "Minimum", behot: "Per board, shown before you pay.", them: `Shown as US$2 when checked on ${REVIEWED}.` },
      { dimension: "Behind the listing", behot: "Public profile, Marketplace services, Promote, Earn, Referral and Fund.", them: "The leaderboard entry." },
      { dimension: "Languages", behot: "Eight languages.", them: "Check the current public experience." },
    ],
    faq: [
      ["Is Outbid.to cheaper?", `Its entry bid was lower when checked on ${REVIEWED}, but a lower entry on a single board is not the same as a position on a category board people browse for your niche.`],
      ["Can I list a profile on Outbid.to?", "Its public model is a single bid driven leaderboard. BeHot.Now is built around profiles as well as listings."],
    ],
    related: ["outbid-lol", "paytorank", "outbidquest"],
  },
  {
    slug: "outbidquest",
    brand: "Outbid.quest",
    title: "BeHot.Now vs Outbid.quest — Product Leaderboard Comparison",
    h1: "BeHot.Now or Outbid.quest?",
    description:
      "Compare BeHot.Now and Outbid.quest: product leaderboard pricing, ranking rules, category structure and what a listing connects to.",
    keywords: ["outbid.quest alternative", "outbid quest comparison"],
    source: "https://outbid.quest/",
    reviewed: REVIEWED,
    intro:
      "Outbid.quest is a public product leaderboard where rank follows the bid amount.",
    summary: [
      `New spots were described as starting at US$5 when checked on ${REVIEWED}.`,
      "BeHot.Now covers products too, but products are one board among many, and a product listing can connect to the founder's profile, to services and to the funding layer.",
    ],
    rows: [
      { dimension: "Focus", behot: "Products plus creators, music, brands, events, gaming and more.", them: "Product leaderboard." },
      { dimension: "Ranking rule", behot: "Heat behind the listing.", them: "Rank follows bid amount." },
      { dimension: "Entry", behot: "Per board minimum boost.", them: `Described as starting at US$5 when checked on ${REVIEWED}.` },
      { dimension: "Fan participation", behot: "Anyone can boost a listing.", them: "Owner driven bidding." },
    ],
    faq: [
      ["Which is better for a product launch?", "If you only want a product leaderboard, either works. BeHot.Now adds a profile, services and a funding layer around the launch."],
      ["Do prices change?", "Yes, on every platform in this category. Always check the current rules page."],
    ],
    related: ["outbid-lol", "productleaderboards", "outbidto"],
  },
  {
    slug: "winning",
    brand: "Winning.com",
    title: "BeHot.Now vs Winning.com — Category Leaderboard Comparison",
    h1: "BeHot.Now or Winning.com?",
    description:
      "Compare BeHot.Now and Winning.com: category based paid leaderboards, minimum bids, ranking rules and what sits behind a listing.",
    keywords: ["winning.com alternative", "paid leaderboard", "category leaderboard"],
    source: "https://winning.com/rules.php",
    reviewed: REVIEWED,
    intro:
      "Winning.com runs category based public leaderboards. Its rules state that rank is highest bid first, with a minimum bid starting at US$1 once a category has a listing.",
    summary: [
      "The category structure is the closest thing to BeHot.Now in this list.",
      "The difference is depth: BeHot.Now listings connect to indexed public profiles, a services marketplace, promotion campaigns, an earning wallet with withdrawals, a referral program and funding, in eight languages.",
    ],
    rows: [
      { dimension: "Boards", behot: "Category specific boards across many verticals.", them: "Category based public leaderboards." },
      { dimension: "Ranking rule", behot: "Heat behind the listing, owner and fan boosts.", them: "Highest bid first according to the rules page." },
      { dimension: "Minimum", behot: "Per board, shown before you pay.", them: `Stated as starting at US$1 once a category has a listing, when checked on ${REVIEWED}.` },
      { dimension: "Ecosystem", behot: "Profiles, Marketplace, Promote, Earn, Referral, Fund.", them: "Leaderboard placement." },
    ],
    faq: [
      ["Is a lower minimum better?", "It is cheaper to enter, but the value of a position depends on how many people browse that board and what the listing can convert into."],
      ["Do both show the bid publicly?", "Both surface position publicly. Check each platform's current rules for exactly what is displayed."],
    ],
    related: ["outbid-lol", "paytorank", "productleaderboards"],
  },
  {
    slug: "topapp",
    brand: "TopApp",
    title: "TopApp Alternative — BeHot.Now vs TopApp App Leaderboard",
    h1: "Looking for a TopApp alternative?",
    description:
      "Compare BeHot.Now and TopApp for promoting an app: leaderboard pricing, category boards, fan boosts and what a listing connects to.",
    keywords: ["topapp alternative", "app leaderboard alternative", "promote app leaderboard"],
    source: "https://topapp.lol/",
    reviewed: REVIEWED,
    intro:
      "TopApp is an app focused leaderboard in the Outbid style. It showed a US$5 minimum when checked.",
    summary: [
      "If you only ship apps, an app-only board is a reasonable place to be.",
      "BeHot.Now runs a dedicated HOT Apps board plus neighbouring boards for products, AI, startups and gaming, so one account covers everything you launch, and each listing links to a public profile you control.",
    ],
    rows: [
      { dimension: "Scope", behot: "Apps board plus products, AI, startups, gaming and more.", them: "App focused leaderboard." },
      { dimension: "Entry", behot: "Per board minimum boost, paid from your wallet.", them: `US$5 minimum shown when checked on ${REVIEWED}.` },
      { dimension: "Fan boosts", behot: "Users can boost any listing.", them: "Owner driven bidding." },
      { dimension: "After the click", behot: "Profile, services, promotion and payouts.", them: "Destination link." },
    ],
    faq: [
      ["Where should an indie app list?", "Both work. BeHot.Now is the better fit if you also want a public profile, services and other boards for future projects."],
      ["Does app store ranking matter?", "Not for either board. Position is decided by what is paid."],
    ],
    related: ["outbid-lol", "productleaderboards", "producthunt"],
  },
  {
    slug: "producthunt",
    brand: "Product Hunt",
    title: "Product Hunt Alternative — BeHot.Now vs Product Hunt",
    h1: "BeHot.Now or Product Hunt?",
    description:
      "Compare BeHot.Now and Product Hunt: community launch day versus a continuous paid leaderboard, and what each one is actually good at.",
    keywords: ["Product Hunt alternative", "Product Hunt competitor", "launch platform alternative"],
    source: "https://www.producthunt.com/launch/how-product-hunt-works",
    reviewed: REVIEWED,
    intro:
      "Product Hunt is a community product launch platform. Its homepage leaderboard uses community and algorithmic signals, and Product Hunt states that launching is free and that paid ads are separate from ranking.",
    summary: [
      "They solve different problems. Product Hunt is a launch day: a concentrated burst of community attention that you cannot buy directly.",
      "BeHot.Now is continuous: you can list any day, buy a position outright, keep it as long as you fund it and boost again whenever you have something to say. You are not competing for one morning.",
      "Many teams do both — launch on Product Hunt, then hold a HOT board position for the months afterwards.",
    ],
    rows: [
      { dimension: "Model", behot: "Continuous paid leaderboard.", them: "Community launch platform with a daily leaderboard." },
      { dimension: "Ranking", behot: "Heat behind the listing.", them: "Community and algorithmic signals; Product Hunt states ads are separate from ranking." },
      { dimension: "Cost to appear", behot: "Per board minimum boost.", them: "Product Hunt states launching is free." },
      { dimension: "Timing", behot: "List any day; boosts run for the board duration.", them: "Launch day driven." },
      { dimension: "Scope", behot: "Products, apps, AI, creators, music, brands, events and more.", them: "Products and tech launches." },
    ],
    faq: [
      ["Is BeHot.Now a Product Hunt replacement?", "Not a replacement — a different mechanism. Product Hunt gives you a launch moment; BeHot.Now gives you a position you can hold."],
      ["Can I be on both?", "Yes, and it is a common pattern."],
      ["Is Product Hunt free?", `Product Hunt states that launching is free and that paid ads are separate from ranking, checked on ${REVIEWED}.`],
    ],
    related: ["outbid-lol", "productleaderboards", "topapp"],
  },
  {
    slug: "paytorank",
    brand: "pay to rank leaderboards",
    title: "Pay to Rank Leaderboard Alternatives — Compared | BeHot.Now",
    h1: "Looking for a pay to rank leaderboard?",
    description:
      "A plain comparison of pay to rank leaderboards: how bidding for the top spot works, what each platform charges and what you get beyond the position.",
    keywords: ["pay to rank leaderboard", "paid leaderboard", "bid for top spot", "buy top spot website"],
    reviewed: REVIEWED,
    intro:
      "Pay to rank is a simple idea: a public list, ordered by what people pay. It works because it is honest — you can see exactly what the top spot costs and exactly who holds it.",
    summary: [
      "The platforms in this category differ mostly in three things: how many boards they run, what you are allowed to list and whether anything exists behind the listing.",
      "BeHot.Now runs category boards, allows a wide range of entity types and attaches a public profile, a services marketplace, promotion, earning, referrals and funding to every account.",
      "It also allows fan boosts: supporters can add Heat to a listing, so a community can push something up without the owner paying for all of it.",
    ],
    rows: [
      { dimension: "Boards", behot: "Category boards across many verticals.", them: "Usually one board, sometimes a few categories." },
      { dimension: "Who can push you up", behot: "You and anyone who backs you.", them: "Usually only the listing owner." },
      { dimension: "What is listed", behot: "Products, apps, startups, AI, creators, music, brands, businesses, events, gaming, social.", them: "Often limited to a website or a handle." },
      { dimension: "Beyond the position", behot: "Profile, Marketplace, Promote, Earn, Referral, Fund.", them: "Typically nothing." },
      { dimension: "Payment", behot: "Wallet based, invoiced in AED.", them: "Usually card checkout per bid." },
    ],
    faq: [
      ["Is pay to rank the same as advertising?", "It is a form of paid placement, but the price, the position and often the click count are public, which most ad platforms do not show."],
      ["Does paying more always win?", "On most of these platforms, yes. On BeHot.Now, position follows the total Heat behind a listing, which can include Heat from supporters."],
      ["Is it worth it?", "It depends on your destination. A board position sends real clicks; whether they convert depends on the page they land on."],
    ],
    related: ["outbid-lol", "productleaderboards", "winning"],
  },
  {
    slug: "productleaderboards",
    brand: "product leaderboards",
    title: "Product Leaderboard Alternatives — Where Products Compete | BeHot.Now",
    h1: "Where can products compete for attention?",
    description:
      "Compare the places a product, app or startup can compete for attention: paid leaderboards, launch platforms and category boards, with pricing and trade-offs.",
    keywords: ["product leaderboard", "startup leaderboard", "app leaderboard", "where to launch a product"],
    reviewed: REVIEWED,
    intro:
      "If you have shipped something, the question is not whether to promote it but where. These are the realistic options and what each one is good at.",
    summary: [
      "Launch platforms give you one concentrated day driven by community votes. Paid leaderboards give you a position you buy directly and can hold. Directories give you a permanent listing nobody browses.",
      "BeHot.Now is a paid leaderboard with category boards, so a product competes against comparable products rather than against everything on the internet.",
      "It also connects the listing to a profile, to services and to funding, so a click can turn into more than a pageview.",
    ],
    rows: [
      { dimension: "Launch platforms", behot: "Complementary — launch there, hold a board position here.", them: "One high intensity day, community driven ranking." },
      { dimension: "Paid leaderboards", behot: "Category boards, fan boosts, wallet payments, connected ecosystem.", them: "Usually one board, owner only bidding." },
      { dimension: "Directories", behot: "Boards expire and reorder, so the top is current.", them: "Permanent listing, little browsing traffic." },
      { dimension: "Ad platforms", behot: "Public price, public position, public clicks.", them: "Private auction, opaque delivery." },
    ],
    faq: [
      ["Which board should a startup use?", "HOT Startups for the company, HOT Products or HOT Apps for the thing you ship, HOT AI if it is an AI product."],
      ["Can I be on more than one board?", "Yes, each listing is separate and holds its own position."],
      ["How long does a position last?", "For the boost duration of that board. You can top it up at any time."],
    ],
    related: ["outbid-lol", "paytorank", "producthunt", "topapp"],
  },
];

export const HOT_COMPARISON_SLUGS = HOT_COMPARISONS.map((c) => c.slug);

export function getHotComparison(slug: string): HotComparison | undefined {
  return HOT_COMPARISONS.find((c) => c.slug === slug);
}
